import React, { useState, useEffect, useMemo, useCallback, useRef, RefObject, ForwardedRef } from "react";
import clsx from "clsx";
import { Calendar as MuiCalendar } from "@material-ui/pickers";
import { createStyles, makeStyles, Theme, useTheme } from "@material-ui/core/styles";

import moment from "moment";
import {
  Typography,
  List,
  ListItem,
  Grid,
  ListItemProps,
  ListProps,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  useMediaQuery,
} from "@material-ui/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { MachineTest, MachineTestFrequency } from "../../../models/machine-test.model";
import TestFrequencyIcon from "../Icons/TestFrequencyIcon";
import { MachineEvent } from "../../../models/machine-event.model";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import Badge from "../Badge/Badge";
import { MachineLog } from "../../../models/machine-log.model";
import { enumToLookup } from "../../../models/lookup.model";
import { CalendarTodayOutlined, FilterList } from "@material-ui/icons";
import IconButton from "../IconButton/IconButton";
import { MachineTestResult, WidgetTestResult } from "../../../models/machine-test-result.model";
import { TestRunStatus } from "../../../models/test-run.model";
import ImageWithText from "../ImageWithText/ImageWithText";
import CalendarSvg from "../Svg/Calendar";
import { Components, Virtuoso } from "react-virtuoso";
import { CalendarLegend } from "./CalendarLegend";
import { CALENDAR_STATUS_OPTIONS } from "./calendarStatusConfig";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DeviceTest } from "../../../models/device-test.model";
import useSearchbarService from "../../../hooks/searchbar-service.hook";
import { StatusType, getStatusDefinition, STATUS_PALETTE_DISPLAY_ORDER } from "../../../constants/status";
import {
  aggregateCalendarComplianceGrid,
  aggregateCalendarResultGrid,
  aggregateDeviceTestGroupComplianceStatus,
  aggregateTestResultDayStatus,
  frequencyDaysFromLabel,
  getStatusDotColor,
  resolveThemeMode,
} from "../../../utils/statusUtils";
import { CalendarComplianceRow } from "../../../models/device-test.model";
import TestStatusChip from "../Status/StatusChip";

interface CalendarListItem {
  item: any;
  key: number | string;
  type: "test" | "testResult" | "event" | "log";
  label: string;
  facilityName?: string;
  caption: string;
  badgeColor?: string;
  frequency?: number;
  /** Precise compliance/result status for test and testResult rows (Story #12571) - undefined for event/log rows. */
  status?: StatusType;
}

export interface CalendarProps {
  tests: DeviceTest[];
  testsTotal: number;
  testResults?: WidgetTestResult[];
  /**
   * Date-wise / frequency-wise compliance grid (Story #12571 AC 3) - one row per machine / day /
   * frequency, same grid as the Audit Report Summary. When supplied, the compliance dot on every
   * tile is derived from these rows (so historical cycles show their retroactive status), instead
   * of only the single next due date carried by `tests`. `tests` is still used for the drawer's
   * run-test actions.
   */
  complianceGrid?: CalendarComplianceRow[];
  events?: MachineEvent[];
  logs?: MachineLog[];
  value: MaterialUiPickersDate;
  onValueChange: (date: MaterialUiPickersDate) => void;
  onMonthChange: (date: MaterialUiPickersDate) => void;
  /** `status` is set when the row came from the compliance grid - the drawer shows it for every test. */
  onSelectMachineTests: (machine: { name: string; tests: MachineTest[]; status?: StatusType }) => void;
  onSelectMachineTestResults?: (machine: { name: string; id: number; frequency?: number }) => void;
  onSelectMachineEvent?: (event: MachineEvent) => void;
  /** Returns true when the user may not open this event (e.g. no schedule edit permission). */
  disableMachineEvent?: (event: MachineEvent) => boolean;
  onSelectMachineLog?: (log: MachineLog) => void;
  actions?: React.ReactNode;
  eventListStyles?: CSSProperties;
  dateFormat: string;
}

const machineTestFrequencies = enumToLookup(MachineTestFrequency);

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      position: "relative",
      [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        flexWrap: "none",
      },
    },
    calendar: {
      marginTop: "-4px", // Offset the weird margin atop the calendar
      // Don't grow past the grid's own content width - it's a fixed-size grid, so growing
      // the wrapper just centers it in dead space instead of making it look bigger.
      flex: "0 0 auto",
      zIndex: 0, // Fix stupid next/previous month buttons showing on top of popups

      marginRight: theme.spacing(5),

      [theme.breakpoints.down("md")]: {
        marginRight: 0,
        marginBottom: theme.spacing(2),
        width: "100%",
      },

      // Bump up @material-ui/pickers' default 36px day cells for a bigger calendar grid.
      // The grid itself is a fixed intrinsic width (it doesn't stretch to fill its flex
      // container), so this - not the outer widget/container width - is what actually
      // controls how big the calendar looks.
      "& .MuiPickersDay-day": {
        width: 62,
        height: 62,
        fontSize: theme.typography.body1.fontSize,
        margin: "0 6px",
      },
      "& .MuiPickersCalendarHeader-dayLabel": {
        width: 62,
        margin: "0 6px",
      },
      "& .MuiPickersCalendar-transitionContainer": {
        minHeight: 62 * 6,
      },
    },
    eventBadge: {
      zIndex: 0 // HACK to keep the circle and square indicators from showing on top of the legend popup
    },
    dayWithEventsContainer: {
      position: "relative",
    },
    dayEventDotList: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      width: "100%",
      pointerEvents: "none",
      bottom: "5px",
      "&>*:not(:last-child)": {
        marginRight: "1px",
      },
    },
    dayEventDot: {
      width: "0.75em",
      height: "0.75em",
    },
    // No backgroundColor here (unlike machineEventDot/machineLogDot below) - the inline
    // `style={{ background: dot.color }}` in renderDay always wins, and that color always comes
    // from getStatusDotColor for these two dot types. Leaving a class-level color here would read
    // as a second, dead source of truth for exactly the colors this ticket audited.
    machineTestDot: {
      borderRadius: "0.75em",
    },

    machineEventDot: {
      backgroundColor: theme.palette.warning.main,
    },
    machineTestResultDot: {
      borderRadius: "0.75em",
    },
    machineLogDot: {
      backgroundColor: theme.palette.info.main,
    },

    // Have to make this look good next to mui calendar, so some strange margins/padding to follow...
    events: {
      flex: "1 1 0%", // takes whatever room the fixed-size calendar doesn't need
      // No explicit height - `root` is a flex row with default align-items:stretch, so this
      // naturally stretches to match the calendar grid's actual height. A fixed height here
      // used to override that and leave blank space below the list once the calendar grew
      // taller than 320px.
      display: "flex",
      flexDirection: "column",

      // Below md, `root` stacks the list under the calendar (flex column), so there is no row to
      // stretch against and "1 1 0%" collapses to 0px - the virtualized (react-virtuoso) list then
      // renders no rows. Give it a real height instead.
      [theme.breakpoints.down("md")]: {
        flex: "0 0 auto",
        height: "480px",
      },
    },
    eventsHeader: {
      height: 40,
      display: "flex",
      alignItems: "center",
      marginBottom: 4.667,
      color: theme.palette.primary.main,
    },
    eventsList: {
      height: "100%",
      marginTop: "12px",
      marginLeft: -theme.spacing(2), // Offset list with padding of items
      marginRight: -theme.spacing(2),

      // With `events` at a fixed height (see above), fill what's left under the header/caption
      // rather than 100% of it, which would overflow the widget.
      [theme.breakpoints.down("md")]: {
        height: "auto",
        flex: "1 1 0%",
        minHeight: 0,
      },
    },
    // Tablet two-column layout (see listColumns): one virtualized row holding up to two items.
    listRow: {
      display: "flex",
    },
    listCell: {
      flex: "0 0 50%",
      width: "50%",
      minWidth: 0,
    },
    // Fills the remaining space below the header row (events is a flex column stretched to the
    // calendar's height) and centers the "Nothing to show"/"No matching items" state within it,
    // both horizontally and vertically - ImageWithText only centers its own content, not itself
    // within extra surrounding space.
    emptyState: {
      flex: "1 1 0%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    event: {
      display: "flex",
      alignItems: "center",
    },
    eventText: {
      marginLeft: theme.spacing(3),
    },
    captionStatusRow: {
      display: "flex",
      alignItems: "center",
    },
    inlineStatusChip: {
      marginLeft: theme.spacing(1),
    },

    legend: {
      flex: "1 1 100%",
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`
    }
  })
);

function Calendar({
  value,
  onValueChange,
  onMonthChange,
  onSelectMachineTests,
  onSelectMachineTestResults,
  onSelectMachineEvent,
  disableMachineEvent,
  onSelectMachineLog,
  tests,
  testsTotal,
  testResults,
  complianceGrid,
  events,
  logs,
  actions,
  eventListStyles,
  dateFormat,
}: CalendarProps) {
  const classes = useStyles();
  const theme = useTheme();
  const mode = resolveThemeMode(theme.palette.type);

  const [testsByDate, setTestsByDate] = useState<{ date: string; tests: DeviceTest[] }[]>([]);
  const [eventsByDate, setEventsByDate] = useState<{ date: string; items: MachineEvent[] }[]>([]);
  const [testResultsByDate, setTestResultsByDate] = useState<{ date: string; items: WidgetTestResult[] }[]>([]);
  const [logsByDate, setLogsByDate] = useState<{ date: string; items: MachineLog[] }[]>([]);
  const { searchbarFilter } = useSearchbarService();
  const today = moment().format(dateFormat);

  // Frontend-only status filter for the day's list (right of the date header). Statuses default
  // to checked - a status is only hidden once explicitly unchecked here, so newly-seen statuses
  // (e.g. after changing the selected day) start visible without needing to reset this map.
  const [statusFilter, setStatusFilter] = useState<Partial<Record<StatusType, boolean>>>({});
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<null | HTMLElement>(null);
  const filterMenuButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tests?.length) {
      setTestsByDate([]);
      return;
    }

    const grouped = tests
      .filter((i) => i.machineActive)
      .map((i) => {
        // Convert to utc here bc the system determines the due date in utc
        const dueUtc = moment.utc(i.dueAt);
        const itemDate = dueUtc.format(dateFormat);
        const dueToday = moment.utc().startOf("date").isAfter(moment.utc(itemDate));

        return {
          date: dueToday ? today : itemDate,
          tests: [i],
        };
      })
      // The Calendar shows nothing for a future date: its compliance window has not opened, so
      // the only status it could carry is "Not Due". Overdue tests are bucketed onto today above,
      // so this only drops tests whose next due date is after today. Calendar-only - QA Details
      // reads the same tests feed directly and keeps its "Not Due" status.
      .filter((g) => !moment(g.date, dateFormat).isAfter(moment(today, dateFormat), "day"))
      // Daily QA is not run on weekends, so a daily test is never shown as "Due" on a Sat/Sun tile
      // (it shows as Due again on Monday). Matches the compliance grid, which the API builds with
      // includeWeekends=false by default. Non-daily tests are unaffected.
      .filter((g) => !(g.tests[0].frequency === 1 && [0, 6].includes(moment(g.date, dateFormat).day())));

    if (!grouped.length) {
      setTestsByDate([]);
      return;
    }

    const byDate = [grouped[0]];

    for (let i = 1; i < grouped.length; i++) {
      const groupedItem = grouped[i];

      const existingItem = byDate.find((x) => x.date === groupedItem.date);

      if (existingItem) {
        existingItem.tests = [...existingItem.tests, ...groupedItem.tests];
        continue;
      }

      byDate.push(groupedItem);
    }

    setTestsByDate(byDate);
  }, [dateFormat, tests, today]);

  useEffect(() => {
    if (!events?.length) {
      setEventsByDate([]);
      return;
    }

    const grouped = events
      .filter((i) => i.active)
      .map((i) => {
        // Convert to utc here bc the system determines the due date in utc
        const dueUtc = moment.utc(i.dueDate);
        const itemDate = dueUtc.format(dateFormat);
        const dueToday = moment.utc().startOf("date").isAfter(moment.utc(itemDate));

        return {
          date: dueToday ? today : itemDate,
          items: [i],
        };
      });

    const byDate = [grouped[0]];

    for (let i = 1; i < grouped.length; i++) {
      const groupedItem = grouped[i];

      const existingItem = byDate.find((x) => x.date === groupedItem.date);

      if (existingItem) {
        existingItem.items = [...existingItem.items, ...groupedItem.items];
        continue;
      }

      byDate.push(groupedItem);
    }

    setEventsByDate(byDate);
  }, [dateFormat, events, today]);

  useEffect(() => {
    if (!testResults?.length) {
      setTestResultsByDate([]);
      return;
    }

    const grouped = testResults.map((i) => {
      // Group by test date as that was saved as utc in our system and the timezone conversion will be correct.
      return {
        date: moment.utc(i.testDate).format(dateFormat),
        items: [i],
      };
    });

    const byDate = [grouped[0]];

    for (let i = 1; i < grouped.length; i++) {
      const groupedItem = grouped[i];

      const existingItem = byDate.find((x) => x.date === groupedItem.date);

      if (existingItem) {
        existingItem.items = [...existingItem.items, ...groupedItem.items];
        continue;
      }

      byDate.push(groupedItem);
    }

    setTestResultsByDate(byDate);
  }, [dateFormat, testResults]);

  useEffect(() => {
    if (!logs?.length) {
      setLogsByDate([]);
      return;
    }

    const grouped = logs
      .filter((i) => i.status.name === "closed" && i.machineLogEvents?.length)
      .map((i) => ({
        // MachineLog.date is a full datetime (see MachineLogDetail.tsx formatting it with
        // defaultDateTimeFormat) - convert to utc for consistency with the tests/events/testResults
        // date-bucketing above.
        date: moment.utc(i.date).format(dateFormat),
        items: [i],
      }));

    const byDate = grouped?.length ? [grouped[0]] : [];

    for (let i = 1; i < grouped.length; i++) {
      const groupedItem = grouped[i];

      const existingItem = byDate.find((x) => x.date === groupedItem.date);

      if (existingItem) {
        existingItem.items = [...existingItem.items, ...groupedItem.items];
        continue;
      }

      byDate.push(groupedItem);
    }

    setLogsByDate(byDate);
  }, [dateFormat, logs]);

  const getEventDotColor = useCallback(
    (items: MachineEvent[]) => {
      const hasOverdue = items.findIndex((i) => moment(today).startOf("date").isAfter(moment(i.dueDate))) >= 0;
      return hasOverdue ? theme.palette.warning.light : theme.palette.warning.main;
    },
    [theme.palette.warning.light, theme.palette.warning.main, today]
  );

  const getLogDotColor = useCallback(
    (items: MachineLog[]) => {
      return theme.palette.success.light;
    },
    [theme.palette.success.light]
  );

  // Channel A - compliance status, derived from tests (DeviceTest[]). Independent of Channel B
  // below so a Fail result dot is never suppressed/overwritten by a Pending/Incomplete compliance
  // status - they render as two separate dots on the same tile (Result Hierarchy Protection).
  // `asOfDate` is the calendar day the group is being rendered for (a tile date, or the selected
  // day) so a test whose dueAt lands on that day reads as "Due" there - not "Not Due" just
  // because dueAt != the real today.
  const getComplianceStatus = useCallback(
    // CALENDAR_STATUS_OPTIONS: a past-due test reads "Overdue" on today's tile (configurable).
    (items: DeviceTest[], asOfDate?: string): StatusType => aggregateDeviceTestGroupComplianceStatus(items, asOfDate, CALENDAR_STATUS_OPTIONS),
    []
  );

  // Story #12571 AC 3 - date-wise / frequency-wise compliance grid (same grid as the Audit Report
  // Summary), bucketed by calendar day. When present it drives the compliance dot on EVERY tile,
  // so historical cycles show their retroactively computed status - not just the single next due
  // date carried by `tests`.
  const complianceGridByDate = useMemo(() => {
    const byDate = new Map<string, CalendarComplianceRow[]>();
    (complianceGrid || []).forEach((row) => {
      // row.date is YYYY-MM-DD from the API; normalise to the calendar's dateFormat.
      const date = moment(row.date, "YYYY-MM-DD").format(dateFormat);
      const bucket = byDate.get(date);
      if (bucket) {
        bucket.push(row);
      } else {
        byDate.set(date, [row]);
      }
    });
    return byDate;
  }, [complianceGrid, dateFormat]);

  const hasComplianceGrid = (complianceGrid?.length ?? 0) > 0;

  // Channel B - result status, derived from testResults (WidgetTestResult[]). Groups by
  // machineId_testId_date internally (see aggregateTestResultDayStatus), so it works whether
  // `items` covers a single day (renderDay) or a single machine+frequency group for the selected
  // day (listItems).
  const getResultStatus = useCallback((items: WidgetTestResult[]): StatusType => aggregateTestResultDayStatus(items, dateFormat), [dateFormat]);

  const handleDateChange = useCallback(
    (date: MaterialUiPickersDate) => {
      return onValueChange(moment(date || new Date()));
    },
    [onValueChange]
  );

  const handleMonthChange = useCallback(
    (date: MaterialUiPickersDate) => {
      const calendarDate = moment(date).format(dateFormat);
      const useToday = moment(calendarDate).format("YYYY-MM") === moment(today).format("YYYY-MM");
      const nextSelectedDate = useToday ? today : calendarDate;

      return onMonthChange(moment(nextSelectedDate));
    },
    [dateFormat, onMonthChange, today]
  );

  const handleSelectMachineTests = useCallback(
    (name: string, tests: MachineTest[], status?: StatusType) => () => {
      return onSelectMachineTests({ name, tests, status });
    },
    [onSelectMachineTests]
  );

  const handleSelectMachineTestResults = useCallback(
    (name: string, id: number, frequency?: number) => () => {
      if (!onSelectMachineTestResults) {
        return;
      }

      return onSelectMachineTestResults({ name, id, frequency });
    },
    [onSelectMachineTestResults]
  );

  const handleSelectMachineLog = useCallback(
    (log: MachineLog) => () => {
      if (!onSelectMachineLog) {
        return;
      }

      return onSelectMachineLog(log);
    },
    [onSelectMachineLog]
  );

  const handleSelectMachineEvent = useCallback(
    (event: MachineEvent) => () => {
      if (!onSelectMachineEvent) {
        return;
      }

      return onSelectMachineEvent(event);
    },
    [onSelectMachineEvent]
  );

  /** https://stackoverflow.com/questions/52709772/put-some-mark-in-specific-date-in-material-ui-using-react */
  const renderDay = useCallback(
    (date: MaterialUiPickersDate, selectedDate: MaterialUiPickersDate, dayInCurrentMonth: boolean, dayComponent: JSX.Element) => {
      if (!date || !dayInCurrentMonth) {
        return <div className={classes.dayWithEventsContainer}>{dayComponent}</div>;
      }

      const calendarDate = moment(date).format(dateFormat);
      // A tile for today or later has not closed its compliance window yet - it is "Due"/"Not
      // Due", never Missed/Incomplete - so any synthesized-Missed grid row for a future date is
      // dropped (matches the future-date guard in getQADetailsStatus).
      const isFutureOrToday = moment(date).startOf("day").isSameOrAfter(moment().startOf("day"), "day");

      const dayTests = testsByDate.find((i) => i.date === calendarDate)?.tests;
      const dayEvents = eventsByDate.find((i) => i.date === calendarDate)?.items;
      const dayResults = testResultsByDate.find((i) => i.date === calendarDate)?.items;
      const dayLogs = logsByDate.find((i) => i.date === calendarDate)?.items;
      const dayGrid = complianceGridByDate.get(calendarDate);

      const dots: { classes: string; color: string }[] = [];

      // Result dot status, kept separate from the compliance dot (AC 4 - the failure marker must
      // survive even on an Incomplete/Pending tile). Where the grid carries a result for the day it
      // wins, so the dot matches the Audit Report (and the list's result rows); the live feed
      // covers days the grid does not (today). Computed first so the compliance dot below can tell
      // when it would only repeat it.
      const dayGridResult = hasComplianceGrid && dayGrid?.length ? aggregateCalendarResultGrid(dayGrid) : null;
      const dayResultStatus = dayGridResult ?? (dayResults?.length ? getResultStatus(dayResults!) : null);

      // Compliance dot. Two feeds:
      //  - the date-wise grid (every historical cycle's retroactive status) drives PAST tiles;
      //  - the single-next-due-date `tests` feed drives TODAY / future tiles ("Due" / "Not Due"),
      //    because the grid is intentionally clamped to yesterday (today's window has not closed,
      //    so it must never show Missed).
      // A tile with grid rows uses the grid; a tile with no grid rows falls back to `tests`.
      // Only an actionable status gets a dot from the grid - a fully complete/not-due day is
      // already represented by its result dot.
      if (hasComplianceGrid && dayGrid?.length) {
        const gridCompliance = aggregateCalendarComplianceGrid(dayGrid);
        // A day whose only compliance issue is a skipped run already shows that skip as its result
        // dot (same status, same color) - a second Skipped dot would just repeat it.
        const duplicatesResultDot = gridCompliance === "skipped" && dayResultStatus === "skipped";
        const suppressed = (isFutureOrToday && (gridCompliance === "missed" || gridCompliance === "incomplete")) || duplicatesResultDot;
        if (!suppressed && gridCompliance !== "complete" && gridCompliance !== "not_due") {
          dots.push({
            classes: `${classes.dayEventDot} ${classes.machineTestDot}`,
            color: getStatusDotColor(gridCompliance, mode),
          });
        }
      } else if (dayTests?.length) {
        dots.push({
          classes: `${classes.dayEventDot} ${classes.machineTestDot}`,
          color: getStatusDotColor(getComplianceStatus(dayTests!, calendarDate), mode),
        });
      }

      if (dayEvents?.length) {
        dots.push({
          classes: `${classes.dayEventDot} ${classes.machineEventDot}`,
          color: getEventDotColor(dayEvents!),
        });
      }

      if (dayResultStatus) {
        dots.push({
          classes: `${classes.dayEventDot} ${classes.machineTestResultDot}`,
          color: getStatusDotColor(dayResultStatus, mode),
        });
      }

      if (dayLogs?.length) {
        dots.push({
          classes: `${classes.dayEventDot} ${classes.machineLogDot}`,
          color: getLogDotColor(dayLogs!),
        });
      }

      if (dots.length) {
        return (
          <div className={classes.dayWithEventsContainer}>
            {dayComponent}
            <div className={classes.dayEventDotList}>
              {dots.map((dot, index) => (
                <div key={index} className={clsx(dot.classes)} style={{ background: dot.color }} />
              ))}
            </div>
          </div>
        );
      }

      return <div className={classes.dayWithEventsContainer}>{dayComponent}</div>;
    },
    [
      dateFormat,
      testsByDate,
      eventsByDate,
      testResultsByDate,
      logsByDate,
      complianceGridByDate,
      hasComplianceGrid,
      classes.dayWithEventsContainer,
      classes.dayEventDot,
      classes.machineTestDot,
      classes.machineEventDot,
      classes.machineTestResultDot,
      classes.machineLogDot,
      classes.dayEventDotList,
      getComplianceStatus,
      getEventDotColor,
      getResultStatus,
      getLogDotColor,
      mode,
    ]
  );

  // Memoize function result by value and side affect of value
  // so it only changes when value changes
  const testsBySelectedDate = useMemo(() => {
    const formattedValue = moment(value).format(dateFormat);

    return testsByDate.find((i) => i.date === formattedValue)?.tests || [];
  }, [value, dateFormat, testsByDate]);

  const eventsBySelectedDate = useMemo(() => {
    const formattedValue = moment(value).format(dateFormat);

    return eventsByDate.find((i) => i.date === formattedValue)?.items || [];
  }, [value, dateFormat, eventsByDate]);

  const testResultsBySelectedDate = useMemo(() => {
    const formattedValue = moment(value).format(dateFormat);

    return testResultsByDate.find((i) => i.date === formattedValue)?.items || [];
  }, [value, dateFormat, testResultsByDate]);

  const logsBySelectedDate = useMemo(() => {
    const formattedValue = moment(value).format(dateFormat);
    return logsByDate.find((i) => i.date === formattedValue)?.items || [];
  }, [value, dateFormat, logsByDate]);

  // Memoize function result by side affect of value
  const testsByMachineFrequency = useMemo(() => {
    const grouped = testsBySelectedDate.map((i) => ({
      key: `test_${i.frequency}_${i.machineId}`,
      frequency: i.frequency,
      frequencyName: machineTestFrequencies.find((freq) => freq.id === i.frequency)?.name,
      machineName: i.machineName,
      facilityName: i.facilityName,
      tests: [i],
    }));

    const byMacFreq = grouped.length ? [grouped[0]] : [];

    for (let i = 1; i < grouped.length; i++) {
      const groupedItem = byMacFreq.find((g) => g.key === grouped[i].key);

      if (groupedItem) {
        groupedItem.tests = [...groupedItem.tests, ...grouped[i].tests];
        continue;
      }

      byMacFreq.push(grouped[i]);
    }

    return byMacFreq;
  }, [testsBySelectedDate]);

  // Story #12571 AC 3 - compliance rows for the selected day, from the date-wise grid. Grouped
  // by machine + frequency label, same as the Audit Report. Only rows with an actionable
  // compliance status (Missed / Incomplete / Pending / Skipped) are surfaced - "Complete"/blank
  // days are represented by the testResults rows instead.
  const complianceGridBySelectedDate = useMemo(() => {
    if (!hasComplianceGrid) {
      return [] as { key: string; machineName: string; facilityName: string; frequencyLabel: string; frequency: number; rows: CalendarComplianceRow[] }[];
    }
    const formattedValue = moment(value).format(dateFormat);
    const rows = complianceGridByDate.get(formattedValue) || [];

    const byKey = new Map<string, { key: string; machineName: string; facilityName: string; frequencyLabel: string; frequency: number; rows: CalendarComplianceRow[] }>();
    for (const row of rows) {
      // Key strictly on the raw label so daily / weekly / monthly rows for one machine never
      // merge - a bad merge is what surfaced a missed weekly as "daily test missed".
      const key = `grid_${(row.frequencyLabel || "").toLowerCase().trim()}_${row.machineId}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        byKey.set(key, {
          key,
          machineName: row.machineName,
          facilityName: row.facilityName,
          frequencyLabel: row.frequencyLabel,
          frequency: frequencyDaysFromLabel(row.frequencyLabel),
          rows: [row],
        });
      }
    }
    return Array.from(byKey.values());
  }, [hasComplianceGrid, value, dateFormat, complianceGridByDate]);

  // Only the groups whose aggregated status is actionable (Missed/Incomplete/Pending/Skipped) -
  // drives both the selected-day list rows and the "N Tests Due" count so they never disagree.
  // For a today/future selected date, Missed/Incomplete are suppressed (window not closed yet).
  const actionableComplianceGroups = useMemo(() => {
    const selectedIsFutureOrToday = moment(value).startOf("day").isSameOrAfter(moment().startOf("day"), "day");
    return complianceGridBySelectedDate.filter((g) => {
      const s = aggregateCalendarComplianceGrid(g.rows);
      if (s === "complete" || s === "not_due") {
        return false;
      }
      if (selectedIsFutureOrToday && (s === "missed" || s === "incomplete")) {
        return false;
      }
      return true;
    });
  }, [complianceGridBySelectedDate, value]);

  // Rows the selected-day compliance list actually shows: actionable grid groups PLUS `tests`
  // feed groups the grid did not cover (today's / future Due tests, since the grid is clamped to
  // yesterday). Used for the empty-state guard - a day with any test row is not "Nothing to show".
  const selectedDayComplianceRowCount = useMemo(() => {
    if (!hasComplianceGrid) {
      return testsBySelectedDate.length;
    }
    const covered = new Set(actionableComplianceGroups.map((g) => `${g.rows[0].machineId}_${g.frequency}`));
    const extra = testsByMachineFrequency.filter((item) => !covered.has(`${item.tests[0]?.machineId}_${item.frequency}`)).length;
    return actionableComplianceGroups.length + extra;
  }, [hasComplianceGrid, actionableComplianceGroups, testsByMachineFrequency, testsBySelectedDate.length]);

  const testResultsByMachineFrequency = useMemo(() => {
    const grouped = testResultsBySelectedDate.map((i) => {
      const frequency = i.frequency || 1;
      return {
        id: i.machineId,
        key: `testresult_${i.frequency || 1}_${i.machineId}`,
        frequency: frequency,
        frequencyName: machineTestFrequencies.find((freq) => freq.id === frequency)?.name,
        machineName: i.machineName,
        facilityName: i.facilityName,
        items: [i],
      };
    });

    const byMacFreq = grouped.length ? [grouped[0]] : [];

    for (let i = 1; i < grouped.length; i++) {
      const groupedItem = byMacFreq.find((g) => g.key === grouped[i].key);

      if (groupedItem) {
        groupedItem.items = [...groupedItem.items, ...grouped[i].items];
        continue;
      }

      byMacFreq.push(grouped[i]);
    }

    return byMacFreq;
  }, [testResultsBySelectedDate]);

  const MemoizedCalendar = useMemo(
    () => (
      <MuiCalendar
        date={value}
        onChange={handleDateChange}
        renderDay={renderDay}
        onMonthChange={handleMonthChange}
        leftArrowButtonProps={{ edge: "start" }}
        rightArrowButtonProps={{ edge: "end" }}
        classes={{ transitionContainer: "{ minHeight: 200 }" }}
      />
    ),
    [value, handleDateChange, renderDay, handleMonthChange]
  );

  const listItems = useMemo(() => {
    const eventListItems: CalendarListItem[] = !events
      ? []
      : eventsBySelectedDate.map((item, index) => ({
        item: item,
        key: `event_${item.id}`,
        type: "event",
        facilityName: item.machine?.facility?.name,
        label: item.machine.name,
        caption: item.title,
        badgeColor: getEventDotColor([item]),
        // Scheduled events on this list are always the still-outstanding occurrence (a
        // completed one shows up as a "log" item instead) - see Legend's "Scheduled Items"
        // Due/Completed pair (Story: schedule status filter).
        status: "due",
      }));

    const logListItems: CalendarListItem[] = !logs
      ? []
      : logsBySelectedDate.map((item, index) => ({
        item: item,
        key: `log_${item.id}`,
        type: "log",
        facilityName: item.machine?.facility?.name,
        label: item.machine.name,
        caption: item.title,
        badgeColor: getLogDotColor([item]),
        // Logs represent an already-recorded/closed scheduled item - the "Completed" half of
        // the Legend's "Scheduled Items" Due/Completed pair.
        status: "complete",
      }));

    // Audit Report result (Pass / Fail / Resolved) per machine + frequency for the selected day.
    // Where the grid has one it wins, so a result row reads the same as the Audit Report; the
    // live-runs aggregation (which also flags subtest "Incomplete", a compliance concern the
    // grid's own row already carries) only covers days the grid does not (today).
    const gridResultByMachineFrequency = new Map<string, StatusType | null>();
    complianceGridBySelectedDate.forEach((group) => {
      gridResultByMachineFrequency.set(`${group.rows[0].machineId}_${group.frequency}`, aggregateCalendarResultGrid(group.rows));
    });

    const testResultListItems: CalendarListItem[] = !testResults
      ? []
      : testResultsByMachineFrequency.map((item, index) => {
        // Check if any test in this group has incomplete subtests
        let skippedCount = 0;
        const groupedByTest: { [key: string]: WidgetTestResult[] } = {};

        item.items.forEach((result) => {
          const key = `${result.machineId}_${result.testId}`;
          if (!groupedByTest[key]) {
            groupedByTest[key] = [];
          }
          groupedByTest[key].push(result);

          // Count skipped tests (status = not_run)
          if (result.status === TestRunStatus.notRun) {
            skippedCount++;
          }
        });

        const resultStatus = gridResultByMachineFrequency.get(`${item.id}_${item.frequency}`) ?? getResultStatus(item.items);

        return {
          item: item,
          key: item.key,
          type: "testResult",
          facilityName: item.facilityName,
          label: item.machineName,
          caption: `${item.items.length} test${item.items.length > 1 ? "s" : ""} done${skippedCount > 0 ? ` - ${skippedCount} Skipped` : ""}`,
          frequency: item.frequency,
          badgeColor: getStatusDotColor(resultStatus, mode),
          status: resultStatus,
        };
      });

    // Grid-sourced result rows: mirrors the day-cell dot's own fallback (renderDay's `dayResults?.length
    // ? ... : hasComplianceGrid && dayGrid?.length ? aggregateCalendarResultGrid(dayGrid) ...`) - when
    // the live testResults feed has nothing for a machine+frequency on this day but the grid can still
    // compute a Pass/Fail/Resolved result for it, surface that as a "testResult" row too. Without this,
    // a day whose dot shows Pass/Fail (from the grid) could have an empty list, since gridComplianceItems
    // below intentionally excludes non-actionable ("complete") grid rows.
    const resultGroupKeys = new Set(testResultsByMachineFrequency.map((g) => `${g.id}_${g.frequency}`));

    const gridResultItems: CalendarListItem[] = [];

    if (hasComplianceGrid) {
      complianceGridBySelectedDate.forEach((group) => {
        if (resultGroupKeys.has(`${group.rows[0].machineId}_${group.frequency}`)) {
          return;
        }

        const gridResult = aggregateCalendarResultGrid(group.rows);
        if (!gridResult) {
          return;
        }

        gridResultItems.push({
          item: { id: group.rows[0].machineId, frequency: group.frequency },
          key: `gridresult_${group.key}`,
          type: "testResult",
          facilityName: group.facilityName,
          label: group.machineName,
          caption: `${group.rows.length} test${group.rows.length > 1 ? "s" : ""} done`,
          frequency: group.frequency,
          badgeColor: getStatusDotColor(gridResult, mode),
          status: gridResult,
        });
      });
    }

    // Compliance rows.
    //  - With the date-wise grid (Story #12571 AC 3): one row per machine+frequency group whose
    //    retroactive status is actionable (Missed/Incomplete/Pending/Skipped).
    //  - PLUS the `tests` feed rows for machine+frequency groups the grid does not cover on this
    //    day - this is how a test that is Due today (grid is clamped to yesterday) still shows as
    //    a "Due" row instead of vanishing.
    //  - Without a grid at all: just the `tests` feed.
    const gridGroupKeys = new Set(actionableComplianceGroups.map((g) => `${g.rows[0].machineId}_${g.frequency}`));

    // A machine+frequency whose result row already reads "Skipped" ("1 test done - 1 Skipped")
    // needs no second "Skipped" compliance row - same fact, listed twice. Its key stays in
    // gridGroupKeys so the tests feed below does not re-add a row for it either.
    const skippedResultKeys = new Set(
      testResultListItems.filter((i) => i.status === "skipped").map((i) => `${i.item.id}_${i.frequency}`)
    );

    const gridComplianceItems: CalendarListItem[] = hasComplianceGrid
      ? actionableComplianceGroups
        .filter(
          (group) =>
            !(aggregateCalendarComplianceGrid(group.rows) === "skipped" && skippedResultKeys.has(`${group.rows[0].machineId}_${group.frequency}`))
        )
        .map((group) => {
        const complianceStatus = aggregateCalendarComplianceGrid(group.rows);
        // Grid rows sit on past dates, but the `tests` feed buckets each test on its dueAt / today,
        // so a selected-day filter would always come back empty here. Use the machine's tests of
        // that frequency regardless of date - they are the tests to run/view for this row.
        const machineTests = (tests || []).filter(
          (t) => t.machineActive && t.machineId === group.rows[0].machineId && t.frequency === group.frequency
        );
        const freqLabel = (group.frequencyLabel || "").trim();
        return {
          item: { key: group.key, tests: machineTests, machineName: group.machineName, complianceStatus },
          key: group.key,
          type: "test" as const,
          facilityName: group.facilityName,
          label: group.machineName,
          caption: freqLabel ? `${freqLabel} test${group.rows.length > 1 ? "s" : ""}` : `test${group.rows.length > 1 ? "s" : ""}`,
          frequency: group.frequency,
          badgeColor: getStatusDotColor(complianceStatus, mode),
          status: complianceStatus,
        };
      })
      : [];

    const selectedDateStr = moment(value).format(dateFormat);

    const testsFeedItems: CalendarListItem[] = !tests
      ? []
      : testsByMachineFrequency
        // When the grid is present, only add `tests` rows the grid did not already cover (e.g.
        // today's Due tests) so we do not double-list a machine+frequency.
        .filter((item) => !hasComplianceGrid || !gridGroupKeys.has(`${item.tests[0]?.machineId}_${item.frequency}`))
        .map((item, index) => {
          // Judge "Due" vs "Not Due" as of the selected calendar day, so a test whose dueAt is
          // that day shows "Due" there instead of "Not Due" (dueAt != real today). See
          // getQADetailsStatus's asOfDate param.
          const complianceStatus = getComplianceStatus(item.tests, selectedDateStr);

          return {
            item: item,
            key: item.key,
            type: "test" as const,
            facilityName: item.facilityName,
            label: item.machineName,
            caption: `${item.tests.length} ${item.frequencyName} test${item.tests.length > 1 ? "s" : ""}`,
            frequency: item.frequency,
            badgeColor: getStatusDotColor(complianceStatus, mode),
            status: complianceStatus,
          };
        });

    return eventListItems
      .concat(logListItems)
      .concat(testResultListItems)
      .concat(gridResultItems)
      .concat(gridComplianceItems)
      .concat(testsFeedItems);
  }, [
    events,
    eventsBySelectedDate,
    getEventDotColor,
    getLogDotColor,
    getComplianceStatus,
    getResultStatus,
    mode,
    logs,
    logsBySelectedDate,
    testResults,
    testResultsByMachineFrequency,
    tests,
    testsByMachineFrequency,
    hasComplianceGrid,
    actionableComplianceGroups,
    complianceGridBySelectedDate,
    value,
    dateFormat,
  ]);

  // The "N Tests Due" caption must match what the list rows actually say - count only the test /
  // grid-compliance rows whose status is actionable (Due / Missed / Incomplete / Pending /
  // Skipped), never a "Not Due" / "Complete" / "Pass" row. Without this the caption said
  // "1 Test Due" while the only row's chip read "Not Due" (a test whose dueAt is a future day the
  // user navigated to).
  const selectedDayTestsDueCount = useMemo(
    () =>
      listItems.filter(
        (i) => (i.type === "test" || i.type === "testResult") && i.status && i.status !== "not_due" && i.status !== "complete" && i.status !== "pass"
      ).length,
    [listItems]
  );

  // Only offer checkboxes for statuses actually present in the selected day's items, ordered to
  // match the existing Legend/status priority ordering.
  const statusCounts = useMemo(() => {
    const counts: Partial<Record<StatusType, number>> = {};
    listItems.forEach((item) => {
      if (item.status) {
        counts[item.status] = (counts[item.status] || 0) + 1;
      }
    });
    return counts;
  }, [listItems]);

  // STATUS_PALETTE_DISPLAY_ORDER is not exhaustive (it omits e.g. "not_due", "complete",
  // "excluded"), so it's only used to order the present statuses - any present status missing
  // from it still needs to show up, just sorted after the ones it does order.
  const availableStatuses = useMemo(() => {
    const present = Object.keys(statusCounts) as StatusType[];
    return present.sort((a, b) => {
      const indexA = STATUS_PALETTE_DISPLAY_ORDER.indexOf(a);
      const indexB = STATUS_PALETTE_DISPLAY_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) {
        return a.localeCompare(b);
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }
      return indexA - indexB;
    });
  }, [statusCounts]);

  const filteredListItems = useMemo(
    () => listItems.filter((item) => !item.status || statusFilter[item.status] !== false),
    [listItems, statusFilter]
  );

  const handleOpenFilterMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setFilterMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseFilterMenu = useCallback(() => {
    setFilterMenuAnchorEl(null);
  }, []);

  const handleToggleStatusFilter = useCallback(
    (status: StatusType) => () => {
      setStatusFilter((prev) => ({ ...prev, [status]: prev[status] === false ? true : false }));
    },
    []
  );

  const renderListItem = useCallback(
    (index: number) => {
      const item = filteredListItems[index];

      return (
        <>
          <Badge className={classes.eventBadge}
            variant={item.type === "event" || item.type === "log" ? "dot" : "dot"}
            shape={item.type === "event" || item.type === "log" ? "square" : undefined}
            htmlColor={item.badgeColor}
          >
            {item.type === "event" || item.type === "log" || !item.frequency ? (
              <CalendarTodayOutlined />
            ) : (
              <TestFrequencyIcon frequency={item.frequency} />
            )}
          </Badge>

          <div className={classes.eventText}>
            <div>{item.label}</div>
            <Typography variant="caption" color="textSecondary">
              {!searchbarFilter.facilityId && <div>{item.facilityName}</div>}
              <span className={classes.captionStatusRow}>
                <span>{item.caption}</span>
                {item.status && (
                  <TestStatusChip
                    status={item.status}
                    label={item.status === "complete" ? "Completed" : undefined}
                    className={classes.inlineStatusChip}
                    // Scheduled Items (event/log rows) belong to the "Scheduled Items Legend", not
                    // the "Tests Legend" - so their Due/Completed chip must use that legend's
                    // colors (see CalendarLegend.tsx "Scheduled Items Legend"), not the Tests
                    // Legend "due" color that getStatusStyle would otherwise pick.
                    colorOverride={
                      item.type === "event"
                        ? { text: theme.palette.warning.light }
                        : item.type === "log"
                        ? { text: theme.palette.success.main }
                        : undefined
                    }
                    disableNativeTitle
                  />
                )}
              </span>
            </Typography>
          </div>
        </>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      classes.eventText,
      classes.eventBadge,
      classes.captionStatusRow,
      classes.inlineStatusChip,
      filteredListItems,
      theme.palette.warning.light,
      theme.palette.success.main,
    ]
  );

  // On tablet the list sits full-width below the calendar (see the `events` style), which leaves
  // room for two items per row. Desktop (list beside the calendar) and phone keep one column.
  const listColumns = useMediaQuery(theme.breakpoints.between("sm", "md")) ? 2 : 1;

  const getListItemClickHandler = useCallback(
    (listItem: typeof filteredListItems[number]) =>
      listItem.type === "event"
        ? handleSelectMachineEvent(listItem.item)
        : listItem.type === "log"
          ? handleSelectMachineLog(listItem.item)
          : listItem.type === "testResult"
            ? handleSelectMachineTestResults(listItem.label, listItem.item.id, listItem.item.frequency)
            : listItem.type === "test"
              ? handleSelectMachineTests(listItem.label, listItem.item.tests, listItem.item.complianceStatus)
              : undefined,
    [handleSelectMachineEvent, handleSelectMachineLog, handleSelectMachineTestResults, handleSelectMachineTests]
  );

  const isListItemDisabled = useCallback(
    (listItem: typeof filteredListItems[number]) => listItem.type === "event" && !!disableMachineEvent?.(listItem.item),
    [disableMachineEvent]
  );

  // Virtuoso renders rows: one item per row, or up to `listColumns` items side by side. Rows (not
  // a fixed-size grid) so each row can still size to its tallest item.
  const renderListRow = useCallback(
    (rowIndex: number) => {
      if (listColumns === 1) {
        return renderListItem(rowIndex);
      }

      const start = rowIndex * listColumns;
      return filteredListItems.slice(start, start + listColumns).map((listItem, offset) => (
        <ListItem key={start + offset} component="div" button className={classes.listCell} disabled={isListItemDisabled(listItem)} onClick={getListItemClickHandler(listItem)}>
          {renderListItem(start + offset)}
        </ListItem>
      ));
    },
    [listColumns, renderListItem, filteredListItems, classes.listCell, getListItemClickHandler, isListItemDisabled]
  );

  const listComponents: Components = useMemo(() => {
    return {
      List: React.forwardRef(({ style, children }: ListProps, listRef: ForwardedRef<HTMLDivElement>) => {
        return (
          <List disablePadding style={{ ...style }} component="div" ref={listRef}>
            {children}
          </List>
        );
      }),

      Item: ({ children, ...props }) => {
        if (listColumns > 1) {
          // Each cell is its own clickable ListItem (see renderListRow).
          return (
            <div {...props} className={classes.listRow}>
              {children}
            </div>
          );
        }

        const index = props["data-item-index"];
        const listItem = filteredListItems[index];

        return (
          <ListItem component="div" {...props} button disabled={isListItemDisabled(listItem)} onClick={getListItemClickHandler(listItem)}>
            {children}
          </ListItem>
        );
      },
    };
  }, [listColumns, classes.listRow, getListItemClickHandler, isListItemDisabled, filteredListItems]);

  return (
    <div id="calendar-container" className={clsx(classes.root)}>
      <div id="calendar" className={clsx(classes.calendar)}>
        {MemoizedCalendar}
      </div>
      <div id="calendar-events" className={classes.events} style={eventListStyles}>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            <Typography variant="body1" className={classes.eventsHeader}>
              {moment(value).format("MMMM Do, YYYY")}
            </Typography>
          </Grid>
          <Grid item>
            <Grid container alignItems="center">
              {availableStatuses.length > 0 && (
                <Grid item ref={filterMenuButtonRef}>
                  <IconButton
                    id="calendar-list-filter-button"
                    aria-label="filter calendar list"
                    aria-haspopup="true"
                    onClick={handleOpenFilterMenu}
                  >
                    <FilterList />
                  </IconButton>
                  <Menu
                    id="calendar-list-filter-menu"
                    keepMounted
                    anchorEl={filterMenuButtonRef.current}
                    open={Boolean(filterMenuAnchorEl)}
                    onClose={handleCloseFilterMenu}
                    disableScrollLock={true}
                    container={filterMenuButtonRef.current}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {availableStatuses.map((status) => (
                      <MenuItem key={status} button onClick={handleToggleStatusFilter(status)}>
                        <ListItemIcon style={{ marginLeft: "-8px", marginRight: "8px" }}>
                          <Checkbox checked={statusFilter[status] !== false} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${status === "complete" ? "Completed" : getStatusDefinition(status).label} (${statusCounts[status] ?? 0})`}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid>
              )}
              {actions && <Grid item>{actions}</Grid>}
            </Grid>
          </Grid>
        </Grid>
        {!testsBySelectedDate?.length &&
        !testResultsBySelectedDate?.length &&
        !eventsBySelectedDate?.length &&
        !logsBySelectedDate?.length &&
        !selectedDayComplianceRowCount ? (
          <div className={classes.emptyState}>
            <ImageWithText image={<CalendarSvg size="80px" />} title="Nothing to show" subheader="There are no calendar items for the selected day." />
          </div>
        ) : !filteredListItems.length ? (
          <div className={classes.emptyState}>
            <ImageWithText image={<CalendarSvg size="80px" />} title="No matching items" subheader="No items match the selected filter. Try adjusting the filter above." />
          </div>
        ) : (
          <>
            <Typography variant="caption" color="textSecondary">
              {tests &&
                `${
                  !hasComplianceGrid && testsTotal >= 100 ? "100+" : selectedDayTestsDueCount
                } Test${selectedDayTestsDueCount !== 1 ? "s" : ""} Due`}
              {testResults && `, ${testResultsBySelectedDate.length} Test Result${testResultsBySelectedDate.length !== 1 ? "s" : ""}`}
              {events && `, ${eventsBySelectedDate.length} Event${eventsBySelectedDate.length !== 1 ? "s" : ""} Due`}
            </Typography>
            <Box className={classes.eventsList}>
              <Virtuoso
                components={listComponents}
                totalCount={Math.ceil(filteredListItems.length / listColumns)}
                itemContent={renderListRow}
              />
            </Box>

            {/*<List component="div" disablePadding>
              {events &&
                eventsBySelectedDate.map((item, index) => (
                  <ListItem key={`event_${index}`} button className={classes.event} onClick={handleSelectMachineEvent(item)}>
                    <Badge variant="dot" shape="square" htmlColor={getEventDotColor([item])}>
                      <CalendarTodayOutlined></CalendarTodayOutlined>
                    </Badge>

                    <div className={classes.eventText}>
                      <div>{item.machine.name}</div>
                      <Typography variant="caption" color="textSecondary">
                        {item.title}
                      </Typography>
                    </div>
                  </ListItem>
                ))}

              {logs &&
                logsBySelectedDate.map((item, index) => (
                  <ListItem key={`log_${index}`} button className={classes.event} onClick={handleSelectMachineLog(item)}>
                    <Badge variant="dot" shape="square" htmlColor={getLogDotColor([item])}>
                      <CalendarTodayOutlined></CalendarTodayOutlined>
                    </Badge>

                    <div className={classes.eventText}>
                      <div>{item.machine.name}</div>
                      <Typography variant="caption" color="textSecondary">
                        <div>{item.title}</div>
                      </Typography>
                    </div>
                  </ListItem>
                ))}

              {testResults &&
                testResultsByMachineFrequency.map((item, index) => (
                  <ListItem key={item.key} button className={classes.event} onClick={handleSelectMachineTestResults(item.machineName, item.items)}>
                    <Badge badgeContent={item.items.length} htmlColor={getTestResultDotColor(item.items)}>
                      <TestFrequencyIcon frequency={item.frequency}></TestFrequencyIcon>
                    </Badge>

                    <div className={classes.eventText}>
                      <div>{item.machineName}</div>
                      <Typography variant="caption" color="textSecondary">
                        {item.items.length} {`test${item.items.length > 1 ? "s" : ""} done`}
                      </Typography>
                    </div>
                  </ListItem>
                ))}

              {tests &&
                testsByMachineFrequency.map((item, index) => (
                  <ListItem key={item.key} button className={classes.event} onClick={handleSelectMachineTests(item.machineName, item.tests)}>
                    <Badge badgeContent={item.tests.length} htmlColor={getTestDotColor(item.tests)}>
                      <TestFrequencyIcon frequency={item.frequency}></TestFrequencyIcon>
                    </Badge>

                    <div className={classes.eventText}>
                      <div>{item.machineName}</div>
                      <Typography variant="caption" color="textSecondary">
                        {item.tests.length} {item.frequencyName} {`test${item.tests.length > 1 ? "s" : ""}`}
                      </Typography>
                    </div>
                  </ListItem>
                ))}
                </List>*/}
          </>
        )}
      </div>
    </div>
  );
}

export default Calendar;
